import {
  QuotationRecord,
  QuotationFilterState,
  QuotationComparisonMatrix,
  QuotationComparisonColumn,
  AiRecommendationInsight,
  QuotationExceptionItem,
  SupplierSelectionPayload,
  QuotationMetricsSummary,
} from '@/types/domain/quotation-comparison';
import { MOCK_QUOTATIONS, MOCK_RFQS } from '@/mocks/database';
import { processQuery, getEntityById, PaginatedResponse } from './pagination';
import { env } from '@/config/env';
import { notifyGlobal } from '@/context/NotificationContext';

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export class MockQuotationComparisonService {
  private records: QuotationRecord[] = MOCK_QUOTATIONS.map((q, idx) => ({
    id: idx === 0 ? 'QUO-3001' : idx === 1 ? 'QUO-3002' : q.id.toUpperCase(),
    quotationNumber: q.quotationNumber.toUpperCase(),
    rfqId: idx < 2 ? 'RFQ-2001' : q.rfqId.toUpperCase(),
    rfqNumber: q.rfqNumber.toUpperCase(),
    vendorId: q.vendorId,
    vendorName: idx === 0 ? 'Lenovo Enterprise Solutions Ltd' : idx === 1 ? 'Dell Global Commercial India Pvt Ltd' : q.vendorName,
    vendorCode: idx === 0 ? 'VND-LENOVO-01' : idx === 1 ? 'VND-DELL-02' : `VND-${q.vendorId.replace('vend-', '')}`,
    sapVendorCode: `SAP-VND-${q.vendorId.replace('vend-', '')}`,
    status: q.isSelected ? 'SELECTED' : 'SUBMITTED',
    submittedAt: q.submittedAt,
    validUntil: q.validUntil,
    currency: q.currency,
    subtotalAmount: q.subtotal,
    discountAmount: q.discountAmount,
    freightAmount: 0,
    taxAmount: q.taxAmount,
    totalAmount: q.totalAmount,
    evaluatedCost: q.totalAmount,
    commercialTerms: {
      paymentTerms: q.paymentTerms,
      deliveryTerms: q.deliveryTerms,
      incoterms: 'DDP',
      warrantyPeriodMonths: 36,
      validityPeriodDays: 60,
      warrantyPeriod: '36 Months',
      validityPeriod: '60 Days',
      freightTerms: 'Included',
      discountPercent: 0,
    },
    items: q.items.map((it, idx) => ({
      id: it.id,
      quotationId: q.id,
      rfqItemId: `rfq-item-${idx + 1}`,
      materialCode: it.materialCode,
      description: it.materialName,
      specification: `Standard enterprise spec model ${it.materialCode}`,
      quantity: it.quantity,
      uom: it.unitOfMeasure,
      unitPrice: it.unitPrice,
      discountAmount: 0,
      taxAmount: Math.round(it.totalPrice * 0.18),
      freightAmount: 0,
      totalPrice: it.totalPrice,
      deliveryDays: it.leadTimeDays,
      technicalCompliance: 'COMPLIANT',
    })),
    technicalScore: q.aiScore || 85,
    commercialScore: 90,
    overallScore: q.aiScore || 85,
    isSelected: q.isSelected,
    selectionJustification: q.isSelected ? 'Recommended by AI Sourcing Engine based on total evaluated cost & compliance score.' : undefined,
  }));

  async getQuotations(params?: Partial<QuotationFilterState>): Promise<PaginatedResponse<QuotationRecord>> {
    await delay(env.mockDelayMs);
    const searchFields: Array<keyof QuotationRecord> = ['quotationNumber', 'rfqNumber', 'vendorName', 'vendorCode'];
    return processQuery(this.records, {
      ...params,
      searchFields,
    });
  }

  async getQuotationsForRfq(rfqId: string): Promise<QuotationRecord[]> {
    await delay(env.mockDelayMs);
    return this.records.filter((q) => q.rfqId === rfqId || q.id === rfqId || rfqId.includes(q.id));
  }

  async getQuotationById(id: string): Promise<QuotationRecord | null> {
    await delay(env.mockDelayMs);
    return (await getEntityById(this.records, id, 'id')) || null;
  }

  async getComparisonMatrix(rfqId: string): Promise<QuotationComparisonMatrix> {
    await delay(env.mockDelayMs);
    const rfqQuotes = this.records.filter((q) => q.rfqId === rfqId || q.id === rfqId);
    const rfq = MOCK_RFQS.find((r) => r.id === rfqId) || MOCK_RFQS[0]!;

    const columns: QuotationComparisonColumn[] = rfqQuotes.map((q, i) => ({
      quotationId: q.id,
      quotationNumber: q.quotationNumber,
      vendorId: q.vendorId,
      vendorName: q.vendorName,
      vendorCode: q.vendorCode,
      totalEvaluatedAmount: q.evaluatedCost,
      currency: q.currency,
      overallRank: i + 1,
      isRecommended: i === 0,
      score: q.overallScore,
      paymentTerms: q.commercialTerms.paymentTerms,
      deliveryLeadTimeDays: 14,
    }));

    return {
      rfqId,
      rfqNumber: rfq.rfqNumber,
      lineItems: rfq.items.map((item, idx) => ({
        lineNumber: (idx + 1) * 10,
        materialCode: item.materialCode,
        description: item.materialName,
        quantity: item.quantity,
        uom: item.unitOfMeasure,
        targetUnitPrice: item.targetUnitPrice || 500,
        quotes: rfqQuotes.map((q) => ({
          quotationId: q.id,
          unitPrice: q.items[idx]?.unitPrice || item.targetUnitPrice || 500,
          totalPrice: q.items[idx]?.totalPrice || (item.targetUnitPrice || 500) * item.quantity,
          variancePercent: 0,
        })),
      })),
      columns,
      recommendedQuotationId: rfqQuotes[0]?.id || 'quo-3001',
    };
  }

  async getAiRecommendation(rfqId: string): Promise<AiRecommendationInsight> {
    await delay(env.mockDelayMs);
    const rfqQuotes = this.records.filter((q) => q.rfqId === rfqId);
    const recQuote = rfqQuotes[0] || this.records[0]!;

    return {
      recommendedQuotationId: 'QUO-3001',
      recommendedVendorId: recQuote.vendorId,
      recommendedVendorName: recQuote.vendorName,
      confidenceScore: 94,
      positiveFactors: ['Lowest total cost of ownership ($12k savings)', 'ISO 9001 verified vendor', 'Preferred Net 60 payment terms'],
      riskFlags: ['Lead time is 2 days longer than secondary bidder'],
      explainabilityBreakdown: 'Recommended via multi-criteria weighted scoring model: Commercial TCO (40%), Technical Compliance (30%), Vendor Scorecard (30%).',
      potentialSavings: 12500,
    };
  }

  async getExceptions(rfqId: string): Promise<QuotationExceptionItem[]> {
    await delay(env.mockDelayMs);
    const base = this.records[0]!;
    return [
      {
        id: `EXC-${rfqId}-1`,
        quotationId: base.id,
        vendorName: base.vendorName,
        category: 'TECHNICAL_DEVIATION',
        severity: 'HIGH',
        description: 'Bid price is 15% lower than target cost baseline. Risk of under-scoping verified.',
      },
    ];
  }

  async finalizeVendorSelection(
    rfqId: string,
    arg2: string,
    arg3?: any
  ): Promise<SupplierSelectionPayload> {
    return this.selectSupplier(rfqId, arg2, arg3);
  }

  async selectSupplier(
    arg1: string,
    arg2?: string,
    arg3?: string
  ): Promise<SupplierSelectionPayload> {
    await delay(env.mockDelayMs);
    const rfqId = arg3 ? arg1 : 'rfq-2001';
    const quotationId = arg3 ? arg2! : arg1;
    const justification = arg3 || arg2 || 'Supplier selected based on commercial evaluation.';

    const quote = await this.getQuotationById(quotationId);
    if (quote) {
      quote.isSelected = true;
      quote.selectionJustification = justification;
      quote.status = 'SELECTED';
    }

    const isAiRec = quotationId === 'quo-3001' || quotationId === this.records[0]?.id;

    notifyGlobal(
      'Supplier Awarded',
      `Selected supplier ${quote?.vendorName || 'Selected Vendor'} for RFQ ${rfqId}.`,
      'StatusChange',
      quotationId
    );

    return {
      rfqId,
      rfqNumber: quote?.rfqNumber || 'RFQ-2001',
      selectedQuotationId: quotationId,
      selectedVendorId: quote?.vendorId || 'vend-1',
      selectedVendorName: quote?.vendorName || 'Nexus Solutions Corp',
      evaluatedTotalAmount: quote?.evaluatedCost || 45000,
      currency: quote?.currency || 'USD',
      isAiRecommended: isAiRec,
      justificationReason: justification,
      selectedAt: new Date().toISOString(),
      selectedBy: 'David Sterling (Procurement Manager)',
    };
  }

  async getMetricsSummary(): Promise<QuotationMetricsSummary> {
    await delay(env.mockDelayMs);
    return {
      totalQuotations: this.records.length,
      activeRfqs: 52,
      evaluationsInProgress: 14,
      evaluationsCompleted: 38,
      totalSavings: 125000,
      aiRecommendationsCount: 12,
    };
  }

  async getQuotationMetrics(): Promise<QuotationMetricsSummary> {
    return this.getMetricsSummary();
  }

  async deleteQuotation(id: string): Promise<boolean> {
    await delay(env.mockDelayMs);
    const initialLen = this.records.length;
    this.records = this.records.filter((r) => r.id !== id && r.quotationNumber !== id);
    return this.records.length < initialLen;
  }

  syncQuotationRecord(quote: QuotationRecord, previousId?: string): QuotationRecord {
    const idx = this.records.findIndex(
      (r) => r.id === quote.id || (previousId && r.id === previousId) || r.quotationNumber === quote.quotationNumber
    );
    if (idx >= 0) {
      const merged = Object.assign({}, this.records[idx], quote);
      this.records.splice(idx, 1);
      this.records.unshift(merged);
      return merged;
    }
    this.records.unshift(quote);
    return quote;
  }
}

export const mockQuotationComparisonService = new MockQuotationComparisonService();
